import React, { useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormField,
  Label,
  Select,
  Text,
  Truncate
} from 'pcln-design-system'
import { Hotels, Key } from 'pcln-icons'
import { clamp, isEmpty, pathOr, pipe } from 'ramda'
import { Calendar } from '@pcln/date-picker'
import { PopoverContainer } from '../Popover'
import TypeAhead from '../type-ahead/TypeAhead'
import DateField from '../date-field'
import { groupDealsURL } from './utils/hotels-url-formatters'
import OpenTripsWithPrepopLink from './OpenTripsWithPrepopLink' // PKG_ADDITIONAL_HOTEL_LINK_NEW
import {
  CalendarWrapperBox,
  createShadowEffectWhen,
  FindYourDealButton,
  ShadowEffect,
  StickyEffect,
  StickyEffectData
} from '../../shared'
import * as PCLNAnalytics from '../../utils/PCLN-Analytics'
import { BootstrapDataContext, ExperimentContext, FormVisualStateContainer } from '../../contexts'
import makeFeedbackAjaxCall from '../../utils/typeahead-feedback'
import styled from 'styled-components'
import { useContainer } from 'unstated-next'
import { ANALYTICS_CATEGORY, ANALYTICS_CATEGORY_HOTELS, CALENDAR_POPOVER_SELECTOR, HOTEL_PRODUCT_TYPE, HotelSearchFormModalTypes } from '../../constants/Constants'
import { TravelerSelection } from '../traveler-selection'
import HotelTravelerSelection from '@pcln/traveler-selection'
import HotelMultiOccInput from './HotelMultiOccInput'
import ConditionalRender, { Experiment } from '../../experiments/ConditionalRender'
import CheckboxProductCopy from './CheckboxProductCopy'
import FreeCancellationCopy from './FreeCancellationCopy'
import { getHotelDateRange } from '../../utils/date-helpers'
import { addDays, format } from 'date-fns'

const config = require('isomorphic-config')

const typeaheadFeedbackURL = config.client.typeahead.feedback.hotels
const typeaheadFeedback = makeFeedbackAjaxCall(typeaheadFeedbackURL)

const roomOptions = [
  { value: '1', label: '1 Room' },
  { value: '2', label: '2 Rooms' },
  { value: '3', label: '3 Rooms' },
  { value: '4', label: '4 Rooms' },
  { value: '5', label: '5 Rooms' },
  { value: '6', label: '6 Rooms' },
  { value: '7', label: '7 Rooms' },
  { value: '8', label: '8 Rooms' },
  { value: '9', label: '9 Rooms' },
  { value: '10', label: '10+ Rooms' }
]

const hotelAgeOptions = () => {
  const options = [
    <option key='default-option' value='default'>
      Select Age
    </option>,
    <option key='infant-option' value='0'>
      Infant
    </option>,
    <option key='1-year-old-option' value='1'>
      1 year old
    </option>
  ]
  for (let i = 2; i < 18; i++) {
    options.push(
      <option key={`${i}-years-old-option`} value={i}>
        {`${i} years old`}
      </option>
    )
  }
  return options
}

const TravelerSelectionDismissButton = ({ disableShadowState, toggleIsControlPanelOpenState }) => {
  const onClick = () => {
    toggleIsControlPanelOpenState()
    disableShadowState()
  }
  return (
    <Box px={3} py={2} mb={2}>
      <Button onClick={onClick} width={1}>
        Done
      </Button>
    </Box>
  )
}

TravelerSelectionDismissButton.propTypes = {
  disableShadowState: PropTypes.func,
  toggleIsControlPanelOpenState: PropTypes.func
}

const StyledButtonContainer = styled(Box)`
  position: relative;
`

// styled Checkbox targets Input node, applied white color to sibling node svg
const StyledCheckbox = styled(Checkbox)`
  ~ svg[data-name='empty']{
    color: ${props => props.isWhiteHotelPlusCarSearch ? 'white' : null};
  }
  :focus ~ svg[data-name='empty']{
    background-color: ${props => props.isWhiteHotelPlusCarSearch ? 'inherit' : null};
  }
`

const startLocationLabel = pathOr(
  'Where from?',
  [ 'contentValues', 'HOMEPAGE', 'HOMEPAGE.PACKAGES.TAB.TYPEAHEAD.STARTLOCATION.PLACEHOLDER', 'value' ]
)

const StyledSelect = styled(Select)`
  cursor: pointer;
  background-color: white;
  -webkit-tap-highlight-color: transparent;
`

const StyledBox = styled(Box)`
  @media (max-width: 360px) {
    padding-right: 0px;
  }
`
const endLocationLabel = pathOr(
  'Where to?',
  [ 'contentValues', 'HOMEPAGE', 'HOMEPAGE.HOTEL.TAB.TYPEAHEAD.ENDLOCATION.PLACEHOLDER', 'value' ]
)
const noop = () => {}
const generateRoomOption = option => {
  const roomOption = option.value > 1 ? `${option.value} Rooms` : `${option.value} Room`
  return <option key={option.value} {...option}>{roomOption}</option>
}

const fireStickyEffectGA = (action, label) => {
  PCLNAnalytics.send({
    action: action,
    data: label
  })
}

function trackItemSelect (item) {
  if (item.isRecent) {
    PCLNAnalytics.send({
      action: 'Hotel Recent Search',
      category: ANALYTICS_CATEGORY,
      data: 'Going to'
    })
  }
  return item
}

export default function HotelsSearchForm (props) {
  const [showSmallCalendar, setShowSmallCalendar] = useState(false)
  const { isStickyInEffect, setIsStickyInEffect } = useContainer(FormVisualStateContainer)
  const [browserWidth, setBrowserWidth] = useState(0)

  const { key,
    isMobile,
    aceContent
  } = useContext(BootstrapDataContext)

  const {
    PKG_ADDITIONAL_HOTEL_LINK_NEW = 'DEFAULT'
  } = useContext(ExperimentContext)
  const isAdditionalHotelLinkSeti = PKG_ADDITIONAL_HOTEL_LINK_NEW !== 'DEFAULT'

  const { isSubmitting } = props

  useEffect(() => {
    const updateBrowserWidth = () => {
      if (props.isOneLineSearch) {
        setShowSmallCalendar(window.innerWidth < 961)
      }
      setBrowserWidth(window.innerWidth)
    }

    updateBrowserWidth()

    window.addEventListener('resize', updateBrowserWidth)
    return () => window.removeEventListener('resize', updateBrowserWidth)
  }, [props.isOneLineSearch])

  useEffect(() => {
    var isValid = Object.keys(props.errors).length === 0
    if (isSubmitting) {
      if (!isValid && isStickyInEffect) {
        fireStickyEffectGA('Hotel Sticky Search Button', 'Invalid')
      } else if (!isValid && !isStickyInEffect) {
        fireStickyEffectGA('Hotel Search Button', 'Invalid')
      }
    }
  }, [isSubmitting, isStickyInEffect, props.errors])

  const handleStickyEffectStateChange = ({ status }) => {
    if (status === StickyEffectData.STATUS_FIXED && !isStickyInEffect) {
      setIsStickyInEffect(true)
      fireStickyEffectGA('Sticky Search', 'Hotel')
    } else if (isStickyInEffect) {
      setIsStickyInEffect(false)
    }
  }

  const isWhiteHotelPlusCarSearch = !isMobile && isStickyInEffect

  const {
    values: {
      startLocation,
      location,
      startDate,
      endDate,
      roomCount = '1',
      isRentalCarNeeded,
      isFlightNeeded,
      adultCount = 2,
      childrenCount,
      childrenAges
    },
    touched,
    dirty,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    handleRoomSelectFocus,
    handleTypeaheadFocus,
    handleDateFocus,
    setFieldValue,
    typeAheadOptions,
    calendarOpen,
    openCalendar,
    closeCalendar,
    autoBotIds: {
      locationSearchProps, // inputProps
      dropdownItemIconProps,
      dropdownItemTextProps,
      datePickerProps,
      selectRoomsProps,
      formButtonProps,
      popoverButtonProps
    },
    onBackButtonClick,
    role,
    id,
    ariaLabel,
    ariaLabelledby,
    onTypeaheadKeyUp,
    modalType,
    withFooter,
    changeTypeAheadConfig,
    handleTravelerSelectionOnFocus,
    currentLocationProps
  } = props

  // Required for scenario when Add a Flight is selected on Hotel Form
  // and you switch to other form tabs and again return to Hotel form
  // We need to trigger the changeTypeAheadConfig manually in that case
  useEffect(() => {
    if (isFlightNeeded) {
      changeTypeAheadConfig('packages')
    } else {
      changeTypeAheadConfig('hotels')
    }
  }, [isFlightNeeded, changeTypeAheadConfig])

  let isOneLineSearch = props.isOneLineSearch
  if (
    (key === 'home' && !isStickyInEffect) ||
    (isFlightNeeded && !isStickyInEffect) ||
    modalType
  ) {
    isOneLineSearch = false
  }

  const flagDateError = !calendarOpen
  const dateError =
    (touched.startDate && errors.startDate) ||
    (touched.endDate && errors.endDate)

  const isSmallScreen = isMobile || browserWidth < 640

  const enableStickyEffect = !isSmallScreen &&
    ((key === 'home') || (key === 'hotels'))

  const showHotelIcon = key === 'home' && isStickyInEffect

  const isLargeInput = !isMobile

  const DesktopShadowEffect = createShadowEffectWhen(!isMobile)

  const typeaheadWidth = isOneLineSearch
    ? isFlightNeeded ? [1, null, 24 / 100] : [1, null, 27 / 100]
    : isFlightNeeded ? [1, null, 1 / 2, null, 1 / 2] : [1, null, 1 / 2, null, 1]

  const calendarWidth = isOneLineSearch
    ? isFlightNeeded ? [1, null, 24 / 100] : [1, null, 31 / 100]
    : isFlightNeeded ? [1, null, 2 / 5, null, 1 / 3] : [1, null, 1 / 2, null, 1 / 3]

  const roomSelectionWidth = isOneLineSearch
    ? isFlightNeeded ? [1, null, 18 / 100] : [1, null, 23 / 100]
    : isFlightNeeded ? [1, null, 2 / 5, null, 1 / 3] : [1, null, 1 / 2, null, 1 / 3]

  const searchButtonWidth = isOneLineSearch
    ? isFlightNeeded ? [1, null, 10 / 100] : [1, null, 19 / 100]
    : isFlightNeeded ? [1, null, 1 / 5, null, 1 / 3] : [1, null, 1 / 2, null, 1 / 3]

  const VP_DISCLAIMER = 'You may only book a package for up to 8 travelers.'

  const computeTravelerSelectionLabel = (adultCount, roomCount) => {
    const travelers = `${adultCount} Adults`
    const rooms = `, ${roomCount} Room`
    return travelers + rooms
  }

  const hotelPlannerLink = groupDealsURL({
    locationObject: {
      itemName: location
    },
    startDate,
    endDate
  })

  const [travelerSelectionLabel, setTravelerSelectionLabel] = useState(() => computeTravelerSelectionLabel(adultCount, roomCount))

  const handleTravelerSelectionChange = setFieldValue => obj => {
    if (obj.childAges) {
      const arr = Object.keys(obj.childAges).map(key => obj.childAges[key])
      setFieldValue('childrenAges', arr)
      return
    }
    switch (obj.travelerType) {
      case 'ROOMS':
        setFieldValue('roomCount', obj.count.toString())
        break
      case 'ADULTS':
        setFieldValue('adultCount', obj.count)
        break
      case 'CHILDREN':
        setFieldValue('childrenCount', obj.count)
        break
    }
  }

  useEffect(() => {
    setTravelerSelectionLabel(computeTravelerSelectionLabel(adultCount, roomCount))
  }, [adultCount, roomCount])

  const handleRentalCarAdditionChange = (e) => {
    setFieldValue('tripType', getTripTypeValue(isFlightNeeded, e.target.checked), false)
    handleChange(e)
    if (e.target.checked) {
      PCLNAnalytics.send({
        action: 'Add Product Checkbox',
        category: key === 'hotels' ? ANALYTICS_CATEGORY_HOTELS : ANALYTICS_CATEGORY,
        data: key === 'hotels' ? 'add car' : 'hotels-add car'
      })
    }
  }

  const handleChildOccReset = () => {
    setFieldValue('childrenCount', 0)
    setFieldValue('childrenAges', [])
  }

  const handleFlightAdditionChange = (e) => {
    setFieldValue('tripType', getTripTypeValue(e.target.checked, isRentalCarNeeded), false)
    handleChange(e)
    changeTypeAheadConfig(e.target.checked ? 'packages' : 'hotels')
    handleChildOccReset()
    if (e.target.checked) {
      PCLNAnalytics.send({
        action: 'Add Product Checkbox',
        category: key === 'hotels' ? ANALYTICS_CATEGORY_HOTELS : ANALYTICS_CATEGORY,
        data: key === 'hotels' ? 'add flight' : 'hotels-add flight'
      })
    }
  }

  const dateLabel = isFlightNeeded ? 'Departing - Returning' : 'Check-in - Check-out'
  const mobileDateLabel = 'Today - Tomorrow'

  const getTripTypeValue = (isFlightNeeded, isRentalCarNeeded) =>
    isFlightNeeded && isRentalCarNeeded
      ? 'STAY:FLY:DRIVE'
      : isRentalCarNeeded
        ? 'STAY:DRIVE'
        : isFlightNeeded ? 'STAY:FLY' : 'STAY'

  const [recentSearches, setRecentSearches] = useState([])
  const [shouldUseRecentSearches, setShouldUseRecentSearches] = useState(true)

  function saveRecent (item) {
    if (isEmpty(item)) {
      return item
    }
    const isDuplicate = recentSearches.some(recent => item.content === recent.content)
    if (!isDuplicate) {
      const itemCopy = { ...item }
      itemCopy.isRecent = true
      const searchesToPreserve = recentSearches.length === 3
        ? recentSearches.slice(0, 2)
        : recentSearches

      setRecentSearches([itemCopy, ...searchesToPreserve])
    }
    return item
  }

  useEffect(() => {
    try {
      const data = JSON.parse(localStorage.getItem('recentHotelSearches'))
      if (data) {
        setRecentSearches(data)
      }
    } catch (e) {
      console.error('error fetching local results') // is this helpful outside of debugging?
    }
  }, [])

  useEffect(() => {
    try {
      localStorage.setItem('recentHotelSearches', JSON.stringify(recentSearches))
    } catch (e) {
      console.error('error setting recent searches') // is this helpful outside of debugging?
    }
  }, [recentSearches])

  const calendarInput = useRef()
  const focusCalendarInput = () => {
    if (calendarInput.current) {
      calendarInput.current.focus()
    }
  }

  const shouldPopulateDates = !startDate && !endDate
  const start = shouldPopulateDates ? format(new Date(), 'yyyyMMdd') : startDate
  const end = shouldPopulateDates ? format(addDays(new Date(), 1), 'yyyyMMdd') : endDate
  const dateValues = getHotelDateRange({ startDate: start, endDate: end })

  useEffect(() => {
    if (shouldPopulateDates) {
      setFieldValue('startDate', start)
      setFieldValue('endDate', end)
    }
  }, [shouldPopulateDates, start, end])

  return (
    <form aria-labelledby={ariaLabelledby} aria-label={ariaLabel} role={role} id={id} onSubmit={handleSubmit}>
      <StickyEffect
        enabled={enableStickyEffect}
        isStickyInEffect={isStickyInEffect}
        handleStickyEffectStateChange={handleStickyEffectStateChange}
        stickyHeight={120}
      >
        <Flex
          mx={-2}
          mb={-3}
          alignItems='center'
          flexWrap={['wrap', isOneLineSearch ? null : 'wrap']}
        >
          {isFlightNeeded && (
            <Box
              width={typeaheadWidth}
              pl={2}
              pr={!isOneLineSearch ? 2 : 1}
              mb={3}
              data-autobot-element-id='DASH_TAB_HOTELS_STARTLOCATION'
              onClick={() => isStickyInEffect ? fireStickyEffectGA('Hotel Sticky Search', 'typeahead')
                : fireStickyEffectGA('Field Click', 'Hotels - typeahead')}
            >
              <ShadowEffect forProperty='startLocation'>
                {({ disableShadowState }) => (
                  <TypeAhead
                    icon={showHotelIcon ? <Hotels color='primary' size={24} /> : null}
                    id='startLocation'
                    name='startLocation'
                    label={startLocationLabel(aceContent)}
                    placeholder={startLocationLabel(aceContent)}
                    value={startLocation}
                    error={touched.startLocation && errors.startLocation}
                    options={typeAheadOptions.startLocation}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={() => handleTypeaheadFocus('typeahead')}
                    setValue={setFieldValue}
                    onItemSelection={pipe(typeaheadFeedback, disableShadowState)}
                    inputProps={locationSearchProps}
                    dropdownItemIconProps={dropdownItemIconProps}
                    dropdownItemTextProps={dropdownItemTextProps}
                    popoverButtonProps={popoverButtonProps}
                    allowEmptySelection={isMobile || (dirty && !!startLocation)}
                    isLargeInput={isLargeInput}
                    onTypeaheadKeyUp={onTypeaheadKeyUp}
                    disableShadowState={disableShadowState}
                    isMobile={isMobile}
                    {...currentLocationProps}
                  />
                )}
              </ShadowEffect>
            </Box>
          )}
          <Box
            width={typeaheadWidth}
            pl={2}
            pr={!isOneLineSearch ? 2 : 1}
            mb={3}
            data-autobot-element-id='DASH_TAB_HOTELS_ENDLOCATION'
            onClick={() => isStickyInEffect ? fireStickyEffectGA('Hotel Sticky Search', 'typeahead')
              : fireStickyEffectGA('Field Click', 'Hotels - typeahead')}
          >
            <ShadowEffect forProperty='location'>
              {({ disableShadowState }) => (
                <TypeAhead
                  icon={showHotelIcon ? <Hotels color='primary' size={24} /> : null}
                  id='location'
                  name='location'
                  label={endLocationLabel(aceContent)}
                  placeholder={endLocationLabel(aceContent)}
                  value={location}
                  error={touched.location && errors.location}
                  options={shouldUseRecentSearches ? recentSearches : typeAheadOptions.location}
                  onChange={e => {
                    handleChange(e)
                    setShouldUseRecentSearches(e.target.value === '')
                  }}
                  onBlur={handleBlur}
                  onFocus={e => {
                    setShouldUseRecentSearches(e.target.value === '')
                    handleTypeaheadFocus('typeahead')
                  }}
                  setValue={setFieldValue}
                  onItemSelection={pipe(trackItemSelect, saveRecent, typeaheadFeedback, disableShadowState)}
                  inputProps={locationSearchProps}
                  dropdownItemIconProps={dropdownItemIconProps}
                  dropdownItemTextProps={dropdownItemTextProps}
                  popoverButtonProps={popoverButtonProps}
                  disableShadowState={disableShadowState}
                  allowEmptySelection={isMobile || (dirty && !!location)}
                  isLargeInput={isLargeInput}
                  onTypeaheadKeyUp={e => {
                    onTypeaheadKeyUp(e)
                  }}
                  isMobile={isMobile}
                  {...currentLocationProps}
                />
              )}
            </ShadowEffect>
          </Box>
          <Box
            width={calendarWidth}
            px={!isOneLineSearch ? 2 : 1}
            mb={3}
            data-autobot-element-id='DASH_TAB_HOTELS_DATES'
            id='hotel-date-range-box'
          >
            <ShadowEffect forProperty='hotel-date-range'>
              {({ disableShadowState }) => (
                <PopoverContainer
                  id='hotel-date-range'
                  open={calendarOpen}
                  data-calendar
                  onDismiss={pipe(closeCalendar, onBackButtonClick, disableShadowState)}
                  domSelectorForVisuallyContainedElement={CALENDAR_POPOVER_SELECTOR}
                  role='application'
                >
                  <DateField
                    name='date-range'
                    label={dateLabel}
                    placeholder={isMobile && modalType !== HotelSearchFormModalTypes.SOPQ && modalType !== HotelSearchFormModalTypes.VIP ? mobileDateLabel : dateLabel}
                    value={dateValues}
                    onClick={openCalendar}
                    onFocus={pipe(openCalendar, handleDateFocus)}
                    error={flagDateError && dateError}
                    {...datePickerProps}
                    isMobile={isMobile}
                    isLargeInput={isLargeInput}
                    aria-expanded={calendarOpen ? 'true' : 'false'}
                    innerRef={calendarInput}
                    type='text'
                  />
                  <CalendarWrapperBox>
                    <Calendar
                      monthsToShow={showSmallCalendar ? 1 : 2}
                      monthWidthPx={300}
                      startDate={startDate}
                      endDate={endDate}
                      maxDaysToSelect={29}
                      onChange={(e, { startDate, endDate }) => {
                        if (startDate && endDate) {
                          focusCalendarInput()
                          closeCalendar()
                          disableShadowState()
                        }
                        setFieldValue('startDate', startDate || '')
                        setFieldValue('endDate', endDate || '')
                      }}
                      useDefaultFooter={withFooter}
                      onDismiss={pipe(focusCalendarInput, closeCalendar, disableShadowState)}
                    />
                  </CalendarWrapperBox>
                </PopoverContainer>
              )}
            </ShadowEffect>
          </Box>
          <Box
            color='text'
            width={roomSelectionWidth}
            px={!isOneLineSearch ? 2 : 1}
            mb={3}
            data-autobot-element-id='DASH_TAB_HOTELS_NUMROOMS'
          >
            {isFlightNeeded && (
              <ShadowEffect enabled={!isMobile} forProperty='travelers'>
                {({ disableShadowState }) => (
                  <TravelerSelection
                    id='travelers'
                    isLargeInput={isLargeInput}
                    isMobile={isMobile}
                    isUsingRoomControl
                    isUsingAdultControl
                    isUsingChildControl
                    isUsingChildAgeControl
                    isUsingControlLabel={false}
                    minAdultCount={1}
                    disclaimer={VP_DISCLAIMER}
                    initNumRooms={clamp(1, 3, Number(roomCount))}
                    initNumAdults={adultCount}
                    initNumChildren={childrenCount || 0}
                    initChildAges={childrenAges || []}
                    initDisplayLabel={travelerSelectionLabel}
                    onDoneButtonClick={disableShadowState}
                    onDismiss={disableShadowState}
                    onChange={handleTravelerSelectionChange(setFieldValue)}
                    isUsingErrorTooltip
                    error={
                      errors.childAge || errors.intlLapInfant || errors.roomCapacity
                    }
                    handleInputOnFocus={() => {
                      if (handleTravelerSelectionOnFocus) {
                        handleTravelerSelectionOnFocus('traveler selection')
                      }
                    }}
                  />
                )}
              </ShadowEffect>
            )}
            {!isFlightNeeded && (
              <HotelMultiOccInput
                modalType={modalType}
                renderRoomSelection={() => (
                  <DesktopShadowEffect forProperty='roomCount'>
                    <FormField>
                      <Label autoHide htmlFor='roomCount' width='auto'>Room</Label>
                      <Key color='primary' style={{ position: 'relative' }} />
                      <StyledSelect
                        id='roomCount'
                        {...selectRoomsProps}
                        name='roomCount'
                        value={roomCount}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onFocus={() => handleRoomSelectFocus('room select')}
                        children={roomOptions.map(generateRoomOption)}
                        style={{ background: 'white', height: isLargeInput ? '56px' : null }}
                      />
                    </FormField>
                  </DesktopShadowEffect>
                )}
                renderTravelerSelection={() => (
                  <ShadowEffect enabled={!isMobile} forProperty='hotel-travelers'>
                    {({ disableShadowState }) => (
                      <HotelTravelerSelection
                        id='hotel-travelers'
                        hotelPlannerLinkAttrs={{
                          href: hotelPlannerLink,
                          target: '_blank'
                        }}
                        isLargeInput={isLargeInput}
                        isUsingRoomControl
                        isUsingAdultControl
                        isUsingChildControl
                        isUsingChildAgeControl
                        isUsingControlLabel={false}
                        minAdultCount={1}
                        // Max number of rooms for hotel search form is 9
                        initNumRooms={clamp(1, 9, Number(roomCount))}
                        initNumAdults={adultCount}
                        initNumChildren={childrenCount || 0}
                        initChildAges={childrenAges || []}
                        initDisplayLabel={travelerSelectionLabel}
                        onDismiss={disableShadowState}
                        onChange={handleTravelerSelectionChange(setFieldValue)}
                        itemizedMaxCounts={{
                          ADULTS: 28,
                          CHILDREN: 8,
                          ROOMS: 9
                        }}
                        childAgeOptions={hotelAgeOptions}
                        initDisplayChildLabel
                        dismissButton={props => TravelerSelectionDismissButton({ ...props, disableShadowState })}
                        isUsingErrorTooltip
                        error={
                          errors.childAge || errors.intlLapInfant || errors.roomCapacity
                        }
                        handleInputOnFocus={() => {
                          if (handleTravelerSelectionOnFocus) {
                            handleTravelerSelectionOnFocus('traveler selection')
                          }
                        }}
                      />
                    )}
                  </ShadowEffect>
                )}
              />
            )}
          </Box>
          {!modalType &&
            <Flex px={2} mb={3} width={[1, null, 1 / 2, null, 1]} alignItems='center' style={{ order: isMobile ? 0 : 1 }}>
              <Text color={isWhiteHotelPlusCarSearch ? 'text.lightest' : 'text.light'} fontSize={0} width='1' pr={[0, null, null, 2]} pb={1}>
                <CheckboxProductCopy>Bundle + Save</CheckboxProductCopy>
              </Text>
              <Flex flexWrap={isAdditionalHotelLinkSeti ? 'wrap' : 'nowrap'} alignItems='center'>
                <StyledBox pr={[1, null, 2, '12px']}>
                  <Label nowrap fontSize={0} color={isWhiteHotelPlusCarSearch ? 'text.lightest' : 'text.light'} htmlFor='packages-need-rental-car' >
                    <StyledCheckbox
                      id='packages-need-rental-car'
                      name='isRentalCarNeeded'
                      checked={isRentalCarNeeded}
                      onChange={handleRentalCarAdditionChange}
                      onBlur={handleBlur}
                      isWhiteHotelPlusCarSearch={isWhiteHotelPlusCarSearch}
                    />
                  &nbsp;Add a car
                  </Label>
                </StyledBox>
                <StyledBox pr={[0, null, null, 2, '12px']}>
                  <Label nowrap fontSize={0} color={isWhiteHotelPlusCarSearch ? 'text.lightest' : 'text.light'} htmlFor='packages-need-flight'>
                    <StyledCheckbox
                      id='packages-need-flight'
                      name='isFlightNeeded'
                      checked={isFlightNeeded}
                      onChange={handleFlightAdditionChange}
                      onBlur={handleBlur}
                      isWhiteHotelPlusCarSearch={isWhiteHotelPlusCarSearch}
                    />
                  &nbsp;Add a flight
                  </Label>
                </StyledBox>
                <ConditionalRender
                  experimentCode='PKG_ADDITIONAL_HOTEL_LINK_NEW'
                  variantValue='SHOW_ADDITIONAL_HOTEL_LINK'
                  triggerExperiment={() => key === 'home'}>
                  <Experiment>
                    <StyledBox mt={['12px', '7px', null, null, 0]} ml={['5px', null, null, null, 1]}>
                      <OpenTripsWithPrepopLink
                        color={isWhiteHotelPlusCarSearch ? 'text.lightest' : 'text.light'}
                        linkText='Add another hotel'
                        onClick={() => {
                          PCLNAnalytics.send({
                            action: 'Add Product Checkbox',
                            data: 'hotels-add hotel'
                          })
                        }}
                        values={{ isMultiHotel: true, ...props.values }}
                      />
                    </StyledBox>
                  </Experiment>
                </ConditionalRender>
              </Flex>
            </Flex>
          }
          <StyledButtonContainer
            pl={!isOneLineSearch ? 2 : 1}
            pr={2}
            mb={3}
            ml='auto'
            width={searchButtonWidth}
            data-autobot-element-id='HOME_HOTELS_SUBMIT_BUTTON'
          >
            <FindYourDealButton {...formButtonProps}
              isLargeInput={isLargeInput}
              currentProduct={HOTEL_PRODUCT_TYPE}>
              <Truncate my={!isOneLineSearch ? null : [1, null, null, null, 0]}>
                {modalType ? 'Search Hotels' : ' Search'}
              </Truncate>
            </FindYourDealButton>
            <FreeCancellationCopy isMobile={isMobile}>
              <Text color='text' fontSize={0}>Book a room with free cancellation for flexibility</Text>
            </FreeCancellationCopy>
          </StyledButtonContainer>
        </Flex>
      </StickyEffect>
    </form>
  )
}

HotelsSearchForm.defaultProps = {
  values: {
    startLocation: '',
    location: '',
    startDate: '',
    endDate: '',
    roomCount: '1',
    isRentalCarNeeded: false,
    isFlightNeeded: false,
    adultCount: 2,
    childrenCount: 0,
    childrenAges: [],
    tripType: 'STAY'
  },
  errors: {},
  touched: {},
  typeAheadOptions: {
    location: [],
    startLocation: []
  },
  ariaLabel: 'hotels_search_form',
  ariaLabelledby: 'hotels_search_form',
  closeCalendar: noop,
  openCalendar: noop,
  autoBotIds: {},
  isOneLineSearch: true,
  handleRoomSelectFocus: noop,
  handleTypeaheadFocus: noop,
  handleDateFocus: noop,
  onTypeaheadKeyUp: noop,
  onBackButtonClick: noop,
  withFooter: true,
  changeTypeAheadConfig: noop,
  currentLocationProps: {
    showCurrentLocation: false,
    isCurrentLocationDisabled: true,
    currentLocation: {},
    handleCurrentLocationPromise: noop
  }
}

HotelsSearchForm.propTypes = {
  values: PropTypes.shape({
    startLocation: PropTypes.string,
    location: PropTypes.string.isRequired,
    locationObject: PropTypes.object,
    startLocationObject: PropTypes.object,
    startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    isRentalCarNeeded: PropTypes.bool.isRequired,
    isFlightNeeded: PropTypes.bool,
    roomCount: PropTypes.string.isRequired,
    adultCount: PropTypes.number,
    childrenCount: PropTypes.number,
    childrenAges: PropTypes.array,
    tripType: PropTypes.string.isRequired
  }).isRequired,
  touched: PropTypes.object.isRequired,
  dirty: PropTypes.bool,
  calendarOpen: PropTypes.bool,
  openCalendar: PropTypes.func,
  closeCalendar: PropTypes.func,
  errors: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  stickyFormState: PropTypes.func.isRequired,
  typeAheadOptions: PropTypes.shape({
    startLocation: PropTypes.array,
    location: PropTypes.array.isRequired
  }),
  autoBotIds: PropTypes.shape({
    locationSearchProps: PropTypes.object, // inputProps
    dropdownItemIconProps: PropTypes.object,
    dropdownItemTextProps: PropTypes.object,
    datePickerProps: PropTypes.object,
    selectRoomsProps: PropTypes.object,
    formButtonProps: PropTypes.object,
    popoverButtonProps: PropTypes.object
  }),
  onBackButtonClick: PropTypes.func,
  isMobile: PropTypes.bool,
  isOneLineSearch: PropTypes.bool,
  handleRoomSelectFocus: PropTypes.func.isRequired,
  handleTypeaheadFocus: PropTypes.func.isRequired,
  handleDateFocus: PropTypes.func.isRequired,
  role: PropTypes.string,
  id: PropTypes.string,
  ariaLabel: PropTypes.string,
  ariaLabelledby: PropTypes.string,
  isSubmitting: PropTypes.bool,
  onTypeaheadKeyUp: PropTypes.func,
  modalType: PropTypes.oneOf([
    HotelSearchFormModalTypes.PRICEBREAKERS,
    HotelSearchFormModalTypes.SOPQ,
    HotelSearchFormModalTypes.VIP
  ]),
  withFooter: PropTypes.bool,
  handleTravelerSelectionOnFocus: PropTypes.func,
  changeTypeAheadConfig: PropTypes.func,
  currentLocationProps: PropTypes.shape({
    showCurrentLocation: PropTypes.bool,
    handleCurrentLocationPromise: PropTypes.func,
    currentLocation: PropTypes.object,
    isCurrentLocationDisabled: PropTypes.bool
  })
}
